/**
 * Recognising "the server is overloaded" as distinct from "something broke".
 *
 * When the backend is saturated, nginx gives up on the upstream and returns 502
 * (bad gateway), 503 (unavailable) or 504 (gateway timeout); a request that
 * outlives the browser's own patience surfaces as a `TypeError: Failed to fetch`
 * with no status at all. None of these mean the user did anything wrong, and
 * none of them are worth a red "error" — the request usually succeeds on a
 * retry a few minutes later.
 *
 * Everything else (400s, 500 from application code) keeps its existing message,
 * because those describe a real problem the user or we need to act on.
 */

/** Statuses nginx returns when it cannot get a timely answer from the app. */
const OVERLOAD_STATUSES = new Set([502, 503, 504]);

export const HIGH_TRAFFIC_MESSAGE =
  'Terminal is experiencing high traffic right now. Please try again in a few minutes.';

/**
 * True when `err` looks like an overload/timeout rather than a real failure.
 *
 * Accepts anything the app throws around: the Error `authenticatedFetch` raises
 * (carrying `status`), an axios-style `{ response: { status } }`, a bare
 * `Response`, or a plain status number.
 */
export function isServerBusyError(err) {
  if (err === null || err === undefined) return false;

  if (typeof err === 'number') return OVERLOAD_STATUSES.has(err);

  const status =
    err.status ??
    (err.response && err.response.status) ??
    (err.data && err.data.status);

  if (OVERLOAD_STATUSES.has(status)) return true;

  // A fetch that never got a response: no status, TypeError from the browser.
  // `status === 0` is what apiInterceptor stamps on network failures.
  const message = String((err && err.message) || '');
  const isNetworkFailure =
    status === 0 ||
    err instanceof TypeError ||
    /failed to fetch|networkerror|network request failed|load failed/i.test(message);

  // A timeout aborted by AbortController surfaces as AbortError.
  const isTimeoutAbort = err.name === 'AbortError' || err.name === 'TimeoutError';

  return isNetworkFailure || isTimeoutAbort;
}

/**
 * The message to show for `err`, or `null` when it is not an overload — callers
 * fall through to their own error handling in that case.
 */
export function serverBusyMessage(err) {
  return isServerBusyError(err) ? HIGH_TRAFFIC_MESSAGE : null;
}

const serverBusy = { isServerBusyError, serverBusyMessage, HIGH_TRAFFIC_MESSAGE };

export default serverBusy;
